import { useCallback, useState } from 'react';
import {
  Inline,
  InlineItem,
  H3,
  Stack,
  Text,
  Secondary,
  UtilityButton,
  Tooltip,
} from '@kinesis/bungle';
import { noop } from 'lodash/fp';
import { formatDistanceStrings, formatDateString } from '@/date';
import { summariseScopes } from '@/scopes';
import { RevokeConfirmation } from './revoke-confirmation';
import type { ConnectedApplication } from '@/api/oauth';

type Props = {
  application: ConnectedApplication;
  now: string;
  onRevoke?: (clientId: string) => void;
};

const ConnectedApplicationItem = ({
  application,
  now,
  onRevoke = noop,
}: Props) => {
  const { client, scopes, last_use: lastUse } = application;
  const [showConfirm, setShowConfirm] = useState(false);

  const onConfirmRevoke = useCallback(() => {
    onRevoke(client.client_id);
  }, [client.client_id, onRevoke]);

  const lastUsedDistance = lastUse ? formatDistanceStrings(now, lastUse) : null;
  const lastUsedDate = lastUse ? formatDateString(lastUse) : null;

  return (
    <Inline>
      <InlineItem sizing='fill-container'>
        <Inline space='small'>
          <InlineItem sizing='fill-container'>
            <Stack space='xsmall'>
              <H3 noWrap>{client.client_name}</H3>
              <Text>
                <Secondary>
                  {lastUsedDistance ? (
                    <>
                      Last used{' '}
                      <Tooltip title={lastUsedDate!} placement='top'>
                        <span>{lastUsedDistance}</span>
                      </Tooltip>
                    </>
                  ) : (
                    <span
                      style={{ color: 'var(--color-text-warning, #b45309)' }}
                    >
                      Never used
                    </span>
                  )}
                </Secondary>
              </Text>
            </Stack>
          </InlineItem>
          <InlineItem>
            <Text>
              <Secondary>{summariseScopes(scopes)}</Secondary>
            </Text>
          </InlineItem>
        </Inline>
      </InlineItem>
      <InlineItem>
        {showConfirm && (
          <RevokeConfirmation
            clientName={client.client_name}
            onRevoke={onConfirmRevoke}
            onClose={() => setShowConfirm(false)}
          />
        )}
        <Tooltip title='Revoke access' placement='top'>
          <UtilityButton
            icon='delete'
            magnitude='small'
            variant='danger'
            onClick={() => setShowConfirm(true)}
          />
        </Tooltip>
      </InlineItem>
    </Inline>
  );
};

export { ConnectedApplicationItem };
